import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/site/Motion";
import { PageHero, SectionHead, CTABand, usePageTitle } from "@/components/site/Bits";

export default function AreaDetail() {
  const { slug } = useParams();
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageTitle(area?.title || "Área de Atuação");

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get(`/areas/${slug}`)
      .then((r) => live && setArea(r.data))
      .catch(() => live && setArea(null))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center bg-ivory pt-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" aria-label="Carregando" />
      </section>
    );
  }

  if (!area) {
    return (
      <section className="flex min-h-[70vh] flex-col items-center justify-center gap-6 bg-ivory px-6 pt-20 text-center">
        <AlertCircle size={36} className="text-gold-deep" strokeWidth={1.25} />
        <p className="font-display text-3xl text-navy">Área não encontrada</p>
        <Link to="/areas-de-atuacao" data-testid="area-back-link" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold-deep">
          <ArrowLeft size={14} /> Ver todas as áreas
        </Link>
      </section>
    );
  }

  return (
    <>
      <PageHero title={area.title} text={area.summary} overline="Área de Atuação" />

      <section className="bg-ivory">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHead num="01" title="Escopo dos serviços" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-base leading-relaxed text-inkgray" data-testid="area-scope">{area.scope}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="border border-navy/10 bg-white p-8 md:p-10">
                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold-deep">Acesso rápido</p>
                <p className="mt-4 text-sm leading-relaxed text-inkgray">
                  Entre em contato para receber orientação adequada à sua necessidade dentro desta área.
                </p>
                <Link to="/contato" data-testid="area-cta-contact" className="mt-6 inline-flex items-center gap-3 bg-navy px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-navy-2">
                  Falar com a Silva Cotan
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:col-start-1">
            <SectionHead num="02" title="Problemas que podem envolver cada situação" />
            <ul className="mt-8 space-y-4" data-testid="area-problems">
              {(area.problems || []).map((p, i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <li className="flex items-start gap-4 border-b border-navy/10 pb-4">
                    <Check size={16} className="mt-0.5 shrink-0 text-gold-deep" strokeWidth={2} />
                    <span className="text-sm leading-relaxed text-inkgray md:text-[15px]">{p}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 lg:row-start-2">
            <SectionHead num="03" title="Formas de atuação" />
            <ol className="mt-8 space-y-5" data-testid="area-approach">
              {(area.approach || []).map((a, i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <li className="flex items-baseline gap-4">
                    <span className="font-display text-lg italic text-gold">0{i + 1}</span>
                    <span className="text-sm leading-relaxed text-inkgray md:text-[15px]">{a}</span>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <CTABand title="Receba orientação técnica sobre a sua situação." button="Entrar em contato" />
    </>
  );
}
